import {
  Link2,
  Linkedin,
  Mail,
  MessageCircle,
  Pencil,
  Plus,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useGetIdentity,
  useNotify,
  useRecordContext,
  useResourceContext,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useEffect, useId, useState, type KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { Bookmark } from "../types";

// Contacts and companies both carry bookmarks.
type RecordWithBookmarks = { id: Identifier; bookmarks?: Bookmark[] };
import {
  bookmarkHostLine,
  bookmarkHref,
  bookmarkPlatform,
  bookmarkSite,
  findDuplicateBookmark,
  groupBookmarks,
  resolveBookmarkInput,
  suggestBookmarkLabel,
  type BookmarkPlatform,
} from "./bookmarks";

const PLATFORM_ICONS: Record<BookmarkPlatform, LucideIcon> = {
  linkedin: Linkedin,
  gmail: Mail,
  whatsapp: MessageCircle,
  other: Link2,
};

// Brand-tinted squares so a group reads at a glance; "other" stays neutral.
const PLATFORM_COLORS: Record<BookmarkPlatform, string> = {
  linkedin: "bg-[#0a66c2] text-white",
  gmail: "bg-[#d93025] text-white",
  whatsapp: "bg-[#1f9d55] text-white",
  other: "bg-muted-foreground text-background",
};

const PlatformIcon = ({ platform }: { platform: BookmarkPlatform }) => {
  const Icon = PLATFORM_ICONS[platform];
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex size-4 shrink-0 items-center justify-center rounded-[4px]",
        PLATFORM_COLORS[platform],
      )}
    >
      <Icon className="size-2.5" strokeWidth={2.5} />
    </span>
  );
};

/**
 * A contact's or company's saved links to LinkedIn, Gmail, WhatsApp and elsewhere,
 * grouped by platform or site and alphabetical within a group. It writes to whichever
 * resource the page is showing. Editable in place on the show page; read-only where a
 * form also holds the record (the edit page). Desktop aside only for now (Sam,
 * 2026-09-22). LCATOMICZO-83.
 */
export const BookmarksListEdit = ({
  readOnly = false,
}: {
  readOnly?: boolean;
}) => {
  const record = useRecordContext<RecordWithBookmarks>();
  const resource = useResourceContext();
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [update] = useUpdate<RecordWithBookmarks>();
  const [adding, setAdding] = useState(false);
  // Tracked by URL, not object identity: the refetch after a save replaces every
  // object in the array.
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);

  // The remove confirmation quietly lapses if it is not clicked.
  useEffect(() => {
    if (!confirmingRemove) return;
    const timer = setTimeout(() => setConfirmingRemove(null), 4000);
    return () => clearTimeout(timer);
  }, [confirmingRemove]);

  if (!record || !resource) return null;
  const bookmarks = record.bookmarks ?? [];
  const viewerEmail =
    typeof identity?.email === "string" ? identity.email : undefined;

  if (readOnly && bookmarks.length === 0) return null;

  // Every write is optimistic, so the record in context changes at once and the next
  // edit builds on this one. Never undoable: a queued write carries the array it was
  // made with and would overwrite anything added while it waited.
  const save = (next: Bookmark[]) =>
    update(
      resource,
      { id: record.id, data: { bookmarks: next }, previousData: record },
      {
        mutationMode: "optimistic",
        onError: () => notify("crm.bookmarks.save_failed", { type: "error" }),
      },
    );

  const handleAdd = (bookmark: Bookmark) => {
    save([...bookmarks, bookmark]);
    setAdding(false);
  };

  const handleEdit = (originalUrl: string, bookmark: Bookmark) => {
    save(bookmarks.map((b) => (b.url === originalUrl ? bookmark : b)));
    setEditing(null);
  };

  const handleRemove = (url: string) => {
    save(bookmarks.filter((b) => b.url !== url));
    setConfirmingRemove(null);
  };

  const groups = groupBookmarks(bookmarks);

  return (
    <div className="flex flex-col gap-2">
      {adding && (
        <BookmarkForm
          bookmarks={bookmarks}
          onSave={handleAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {groups.map((group) => (
        <div key={group.key} className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <PlatformIcon platform={group.platform} />
            {group.site ??
              translate(`crm.bookmarks.platforms.${group.platform}`)}
          </div>
          {group.bookmarks.map((bookmark, index) =>
            editing === bookmark.url ? (
              <BookmarkForm
                key={`${bookmark.url}-${index}`}
                bookmarks={bookmarks}
                initial={bookmark}
                onSave={(next) => handleEdit(bookmark.url, next)}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <BookmarkRow
                key={`${bookmark.url}-${index}`}
                bookmark={bookmark}
                href={bookmarkHref(bookmark.url, viewerEmail)}
                readOnly={readOnly}
                confirmingRemove={confirmingRemove === bookmark.url}
                onEdit={() => {
                  setConfirmingRemove(null);
                  setAdding(false);
                  setEditing(bookmark.url);
                }}
                onAskRemove={() => setConfirmingRemove(bookmark.url)}
                onRemove={() => handleRemove(bookmark.url)}
              />
            ),
          )}
        </div>
      ))}

      {!readOnly && !adding && (
        <div className="flex flex-col items-start gap-2">
          {bookmarks.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {translate("crm.bookmarks.empty")}
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-9 md:h-6 cursor-pointer"
            onClick={() => {
              setEditing(null);
              setAdding(true);
            }}
          >
            <Plus className="w-4 h-4 md:w-3 md:h-3 mr-1" />
            {translate("crm.bookmarks.add")}
          </Button>
        </div>
      )}
    </div>
  );
};

const BookmarkRow = ({
  bookmark,
  href,
  readOnly,
  confirmingRemove,
  onEdit,
  onAskRemove,
  onRemove,
}: {
  bookmark: Bookmark;
  href: string | null;
  readOnly: boolean;
  confirmingRemove: boolean;
  onEdit: () => void;
  onAskRemove: () => void;
  onRemove: () => void;
}) => {
  const translate = useTranslate();
  const label = (
    <>
      <span className="truncate text-sm">{bookmark.label}</span>
      <span className="truncate text-xs text-muted-foreground">
        {href
          ? bookmarkHostLine(bookmark.url)
          : translate("crm.bookmarks.not_a_link")}
      </span>
    </>
  );

  return (
    <div className="group flex min-w-0 items-center gap-1 rounded-md py-1 pl-5.5 pr-1 hover:bg-muted focus-within:bg-muted">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={bookmark.url}
          className="flex min-w-0 flex-1 flex-col rounded-sm focus-visible:outline-2 focus-visible:outline-ring"
        >
          {label}
        </a>
      ) : (
        // Stored but not http(s): shown, never linked. Claude writes this column
        // directly and the database checks shape, not scheme.
        <span className="flex min-w-0 flex-1 flex-col" title={bookmark.url}>
          {label}
        </span>
      )}
      {!readOnly &&
        (confirmingRemove ? (
          <Button
            variant="destructive"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onRemove}
          >
            {translate("crm.bookmarks.confirm_remove")}
          </Button>
        ) : (
          <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none">
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={translate("crm.bookmarks.edit")}
              title={translate("crm.bookmarks.edit")}
              onClick={onEdit}
            >
              <Pencil className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={translate("crm.bookmarks.remove")}
              title={translate("crm.bookmarks.remove")}
              onClick={onAskRemove}
            >
              <X className="size-3.5" />
            </Button>
          </div>
        ))}
    </div>
  );
};

/**
 * The Add form, and the Edit form when given the bookmark to start from. An edited link
 * goes through the same rules as a new one.
 */
const BookmarkForm = ({
  bookmarks,
  initial,
  onSave,
  onCancel,
}: {
  bookmarks: Bookmark[];
  initial?: Bookmark;
  onSave: (bookmark: Bookmark) => void;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const id = useId();
  const [rawUrl, setRawUrl] = useState(initial?.url ?? "");
  const [label, setLabel] = useState(initial?.label ?? "");
  // The name follows the suggestion until the user types their own. A bookmark being
  // edited already has one, which is kept unless the field is cleared.
  const [labelEdited, setLabelEdited] = useState(!!initial);

  // A link, or a phone number that becomes a WhatsApp link.
  const resolved = resolveBookmarkInput(rawUrl);
  const url = resolved && "url" in resolved ? resolved.url : null;
  const error = resolved && "error" in resolved ? resolved.error : null;
  // The bookmark being edited is not a duplicate of itself.
  const others = initial
    ? bookmarks.filter((b) => b.url !== initial.url)
    : bookmarks;
  const duplicate = url ? findDuplicateBookmark(others, url) : undefined;
  const canSave = !!url && !duplicate && label.trim() !== "";

  const onUrlChange = (value: string) => {
    setRawUrl(value);
    if (!labelEdited) {
      const next = resolveBookmarkInput(value);
      setLabel(next && "url" in next ? suggestBookmarkLabel(next.url) : "");
    }
  };

  const submit = () => {
    if (!canSave || !url) return;
    const next = { label: label.trim(), url };
    // Nothing changed: close without a write.
    if (initial && next.url === initial.url && next.label === initial.label) {
      onCancel();
      return;
    }
    onSave(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    }
  };

  const platform = url ? bookmarkPlatform(url) : null;
  // A link outside the three platforms goes under its site, as in the list.
  const site = url && platform === "other" ? bookmarkSite(url) : null;

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${id}-url`}
          className="text-xs font-medium text-muted-foreground"
        >
          {translate("crm.bookmarks.url")}
        </label>
        <Input
          id={`${id}-url`}
          autoFocus
          className="h-8 text-sm"
          placeholder={translate("crm.bookmarks.url_placeholder")}
          value={rawUrl}
          aria-invalid={!!error || !!duplicate}
          aria-describedby={error || duplicate ? `${id}-url-error` : undefined}
          onChange={(event) => onUrlChange(event.target.value)}
          onKeyDown={onKeyDown}
        />
        {error && (
          <p
            id={`${id}-url-error`}
            role="alert"
            className="text-xs text-destructive"
          >
            {translate(
              error === "no_country_code"
                ? "crm.bookmarks.no_country_code"
                : "crm.bookmarks.invalid_url",
            )}
          </p>
        )}
        {duplicate && (
          <p
            id={`${id}-url-error`}
            role="alert"
            className="text-xs text-destructive"
          >
            {translate("crm.bookmarks.duplicate", {
              label: duplicate.label,
            })}
          </p>
        )}
        {platform && !duplicate && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {translate("crm.bookmarks.goes_under")}
            <PlatformIcon platform={platform} />
            <span className="font-medium text-foreground">
              {site ?? translate(`crm.bookmarks.platforms.${platform}`)}
            </span>
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${id}-label`}
          className="text-xs font-medium text-muted-foreground"
        >
          {translate("crm.bookmarks.name")}
        </label>
        <Input
          id={`${id}-label`}
          className="h-8 text-sm"
          value={label}
          onChange={(event) => {
            setLabel(event.target.value);
            // Cleared, it follows the link's suggestion again.
            setLabelEdited(event.target.value !== "");
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" className="h-7" onClick={onCancel}>
          {translate("crm.bookmarks.cancel")}
        </Button>
        <Button size="sm" className="h-7" disabled={!canSave} onClick={submit}>
          {translate("crm.bookmarks.save")}
        </Button>
      </div>
    </div>
  );
};
